import { coverageFrom } from './orderCoverage.js';

/**
 * What happened to everything the factory ever sold.
 *
 * The backend hands over one flat row per order, drawn from the three places an
 * order can live — open in Redis, delivered in `CompletedOrder`, deleted in a
 * raw Mongo collection. The arithmetic on top of that is here rather than in the
 * page because four things on the screen ask the same question of the same rows
 * and must not answer it differently.
 *
 * The one rule worth stating: a metre has exactly one fate. An order for
 * 10,000 m that produced 4,000, pulled 3,000 off the shelf and has 5,000 promised
 * by a running lot is not 12,000 metres of anything — the promise overlaps the
 * rest. So the fates are taken in the order they become irreversible (made,
 * then taken from stock, then merely promised, then nothing yet) and each one
 * only claims what the ones before it left. Otherwise the totals add up to more
 * than the customer asked for, which is how a chart ends up lying.
 */

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const arr = v => (Array.isArray(v) ? v : []);

/** Where each metre of one order ended up. Sums to `ordered`, always. */
export function fateOf(row) {
  const ordered = num(row?.ordered);

  const produced = Math.min(num(row?.produced), ordered);
  const stock = Math.min(num(row?.stock), ordered - produced);
  const promised = Math.min(num(row?.allocated), ordered - produced - stock);
  const open = Math.max(0, ordered - produced - stock - promised);

  return { ordered, produced, stock, promised, open };
}

export const ORIGIN = {
  open: { label: 'Open', color: 'info' },
  archived: { label: 'Delivered', color: 'success' },
  deleted: { label: 'Deleted', color: 'danger' }
};

const MONTH = (ts, locale) =>
  new Intl.DateTimeFormat(locale || undefined, {
    month: 'short',
    year: '2-digit'
  }).format(new Date(ts));

/**
 * @param rows  the backend's flat order rows
 * @returns {{kpis, fate, byMonth, byClient, byProduct, byState}}
 */
export function rollup(rows, { locale } = {}) {
  const all = arr(rows);

  const kpis = {
    orders: all.length,
    open: 0,
    archived: 0,
    deleted: 0,
    ordered: 0,
    produced: 0,
    stock: 0,
    promised: 0,
    stillOpen: 0,
    cancelled: 0,
    clients: 0,
    lines: 0
  };

  const clients = new Map();
  const products = new Map();
  const months = new Map();
  const states = new Map();

  for (const row of all) {
    const f = fateOf(row);
    const cov = coverageFrom(row);

    kpis[row.origin] = (kpis[row.origin] || 0) + 1;
    kpis.ordered += f.ordered;
    kpis.produced += f.produced;
    kpis.stock += f.stock;
    kpis.promised += f.promised;
    kpis.stillOpen += f.open;
    kpis.cancelled += num(row.cancelledMeters);
    kpis.lines += num(row.lines);

    // A deleted order's state is that it was deleted — its coverage at the
    // moment somebody called it off says nothing anybody needs.
    const state = row.origin === 'deleted' ? 'deleted' : cov.state;
    states.set(state, (states.get(state) || 0) + 1);

    const client = row.client || '—';
    if (!clients.has(client)) {
      clients.set(client, {
        client,
        orders: 0,
        ordered: 0,
        produced: 0,
        stock: 0,
        cancelled: 0
      });
    }
    const c = clients.get(client);
    c.orders += 1;
    c.ordered += f.ordered;
    c.produced += f.produced;
    c.stock += f.stock;
    c.cancelled += num(row.cancelledMeters);

    for (const key of arr(row.products)) {
      if (!products.has(key)) products.set(key, { key, orders: 0, ordered: 0 });
      const p = products.get(key);
      p.orders += 1;
      // An order's metres are not split per product here — the row does not
      // carry that split. This ranks by how often a product is asked for, with
      // the order's size as the weight, and it is labelled that way on screen.
      p.ordered += f.ordered / Math.max(1, arr(row.products).length);
    }

    if (row.createdAt > 0) {
      const d = new Date(row.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!months.has(key)) {
        months.set(key, {
          key,
          label: MONTH(row.createdAt, locale),
          ts: new Date(d.getFullYear(), d.getMonth(), 1).getTime(),
          orders: 0,
          ordered: 0,
          produced: 0,
          stock: 0,
          promised: 0,
          open: 0,
          cancelled: 0
        });
      }
      const m = months.get(key);
      m.orders += 1;
      m.ordered += f.ordered;
      m.produced += f.produced;
      m.stock += f.stock;
      m.promised += f.promised;
      m.open += f.open;
      m.cancelled += num(row.cancelledMeters);
    }
  }

  kpis.clients = clients.size;
  kpis.covered = kpis.produced + kpis.stock;

  return {
    kpis,
    fate: {
      produced: kpis.produced,
      stock: kpis.stock,
      promised: kpis.promised,
      open: kpis.stillOpen,
      cancelled: kpis.cancelled
    },
    byMonth: [...months.values()].sort((a, b) => a.ts - b.ts),
    byClient: [...clients.values()].sort((a, b) => b.ordered - a.ordered),
    byProduct: [...products.values()].sort((a, b) => b.ordered - a.ordered),
    byState: [...states.entries()]
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
  };
}

/** Milliseconds back from now, for the period buttons. */
export const RANGES = [
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '12m', label: '12 months', days: 365 },
  { key: 'all', label: 'All time', days: null }
];

export function windowFor(key, now = Date.now()) {
  const r = RANGES.find(x => x.key === key);
  if (!r || r.days == null) return {};
  return { from: now - r.days * 86400000, to: now };
}
